import React, { useState } from 'react';

// Types
import { CalendarMeeting } from 'Shared/types/CalendarMeeting.type';
import { Member } from 'Shared/types/Member.type';

// Utils
import { getDefaultAvatarColor } from 'Shared/utils/getDefaultAvatarColor';

// Components
import { WpPopover, WpPopoverToggle, WpPopoverContent } from 'Wpds/components/popover';
import { WpAvatar, WpAvatarGroup } from 'Wpds/components/avatar';
import { WpTooltipOverlay, useTooltip } from 'Wpds/components/tooltip';

// Styles
import './DayEventAttendees.scss';

type Props = {
  event: CalendarMeeting;
  members: Member[];
};

const DayEventAttendees = ({ event, members }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const attendeesLength = event.attendees.length;

  const tooltip = useTooltip({ placement: 'top' });

  const formattedAttendeesList = event.attendees.map((attendee, index) => {
    const member = members.find(m => m.email === attendee);

    if (member) return member;
    return {
      id: index,
      email: attendee,
      avatar: {
        color: getDefaultAvatarColor(attendee.slice(0).toLowerCase()),
        initials: attendee.slice(0, 2).toUpperCase(),
        profilePictureUrl: null,
      },
    };
  });

  const baseClassName = 'DayEventAttendees';

  const Attendee = ({ attendee }: { attendee }) => (
    <div className={`${baseClassName}__list__item`}>
      <div className={`${baseClassName}__list__item__avatar`}>
        <WpAvatar avatar={attendee.avatar} size="sm" />
      </div>
      <div className={`${baseClassName}__list__item__label`}>
        {attendee.fullName || attendee.displayName || attendee.email}
      </div>
    </div>
  );

  const AttendeeList = () => (
    <div className={`${baseClassName}__list`}>
      {formattedAttendeesList.map(attendee => (
        <Attendee attendee={attendee} key={`${event.uuid}-attendee-${attendee.email}`} />
      ))}
    </div>
  );

  return (
    <>
      <WpPopover open={isOpen} onOpenChange={setIsOpen} placement="left" showArrow={false} withBoxShadow>
        <WpPopoverToggle>
          <div role="button" className={`${baseClassName}__toggle`} ref={tooltip.targetRef}>
            <WpAvatarGroup users={formattedAttendeesList as any} limit={6} showAdditionalCount />
          </div>
        </WpPopoverToggle>
        <WpPopoverContent>
          <div className={`${baseClassName}__content`}>
            <h5>
              {attendeesLength} Attendee{attendeesLength > 1 ? 's' : ''}
            </h5>
            <AttendeeList />
          </div>
        </WpPopoverContent>
      </WpPopover>
      {!isOpen && <WpTooltipOverlay context={tooltip}>View Attendees</WpTooltipOverlay>}
    </>
  );
};

export default DayEventAttendees;
